import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  onAuthChange,
  signIn as fbSignIn,
  signOut as fbSignOut,
} from "@/lib/firebase/auth";
import { fetchDoc } from "@/lib/firebase/firestore";
import { COLLECTIONS } from "@/lib/firebase/firestore";
import type { AdminUser, AuthContextValue } from "@/types/auth";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Listen to Firebase auth state ──────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthChange(async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Fetch the admin profile from Firestore
      const profile = await fetchDoc<AdminUser>(
        COLLECTIONS.USERS,
        firebaseUser.uid,
      );

      // If the UID has no Firestore profile (i.e. not an admin), treat as signed out
      setUser(profile);
      setLoading(false);
    });

    return unsub;
  }, []);

  // ── signIn ─────────────────────────────────────────────────────────────────
  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      await fbSignIn(email, password);
      // onAuthChange listener above will update `user` automatically
    } catch (err) {
      setLoading(false);
      throw err; // re-throw so LoginPage can catch + display error
    }
  }, []);

  // ── signOut ────────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    await fbSignOut();
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signOut,
    isSuperAdmin: user?.role === "super_admin",
    isFrontDesk: user?.role === "front_desk",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Internal hook (used by useAuth.ts) ─────────────────────────────────────
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error("useAuthContext must be used inside <AuthProvider>");
  return ctx;
}